import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { scrollToId } from "./SmoothScroll";

const ease = [0.7, 0, 0.2, 1];

const MaskLine = ({ children, i, k }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      key={k}
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroImage = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0 0 0)" }}
        animate={{ clipPath: "inset(0% 0 0 0)" }}
        transition={{ duration: 1.6, delay: 0.5, ease }}
        className="relative aspect-[4/5] w-full overflow-hidden"
        data-testid="hero-image-frame"
      >
        <motion.img
          src="/img/model.jpg"
          alt="Éditorial mode ALLURE HOUSE"
          className="absolute inset-0 w-full h-full object-cover grayscale contrast-[1.1]"
          initial={{ scale: 1.25 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 2.4, delay: 0.5, ease }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_30%,rgba(11,10,9,0.75))]" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute bottom-5 left-5 right-5 flex justify-between font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--bone)]">
          <span>N° 001</span><span>SS — 2026</span>
        </div>
      </motion.div>
      <motion.div
        style={{ transform: "translateZ(90px)" }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 1.6, ease }}
        className="absolute -left-10 -bottom-10 w-28 h-28 rounded-full border border-[var(--gold)] flex items-center justify-center bg-[var(--ink)]"
      >
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[spin_24s_linear_infinite]">
          <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <text fontSize="9.5" letterSpacing="3" fill="#C9A96E" fontFamily="JetBrains Mono"><textPath href="#circ">A HOUSE OF IDEAS · A HOUSE OF IDEAS ·</textPath></text>
        </svg>
        <span className="font-display italic text-2xl text-[var(--bone)]">A</span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const bigX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen pt-32 md:pt-40 pb-16 overflow-hidden">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative px-6 md:px-12 grid lg:grid-cols-12 gap-14 lg:gap-10 items-end">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.1 }} className="eyebrow mb-10" data-testid="hero-overline">
            {t.hero.over}
          </motion.p>
          <h1 className="font-display text-[2.9rem] leading-[0.98] sm:text-7xl lg:text-[6.6rem] tracking-[-0.02em]" data-testid="hero-title">
            {t.hero.lines.map((l, i) => (
              <MaskLine key={`${lang}-${i}`} k={`${lang}-${i}`} i={i}>
                {i === 1 ? <em className="italic text-gold font-normal">{l}</em> : l}
              </MaskLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.9, ease }}
            className="mt-10 max-w-lg text-stone text-base md:text-lg leading-relaxed" data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.05, ease }} className="mt-12 flex flex-wrap gap-4">
            <button data-testid="hero-cta-primary" onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-3 rounded-full bg-[var(--gold)] text-[var(--ink)] pl-7 pr-2 py-2 font-semibold hover:bg-[var(--gold-2)] transition-colors duration-300">
              {t.hero.cta}
              <span className="w-10 h-10 rounded-full bg-[var(--ink)] text-[var(--gold)] flex items-center justify-center transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight size={18} /></span>
            </button>
            <button data-testid="hero-cta-secondary" onClick={() => scrollToId("work")} className="inline-flex items-center gap-2 rounded-full border border-line px-7 py-4 hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300">
              {t.hero.cta2}
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yImg }} className="lg:col-span-4 lg:col-start-9 max-w-sm w-full justify-self-center lg:justify-self-end">
          <HeroImage />
        </motion.div>
      </div>
      <motion.div style={{ x: bigX }} className="relative mt-24 whitespace-nowrap font-display text-[22vw] leading-none outline-text select-none pointer-events-none" aria-hidden="true">
        ALLURE HOUSE
      </motion.div>
      <div className="absolute left-6 md:left-12 bottom-8 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-stone">
        <ArrowDownRight size={14} className="text-gold" /> {t.hero.scroll}
      </div>
    </section>
  );
};
